/**
 * The wall's id-list scopes, per window (Part 04 decision 3; Part 09
 * decision 4): Everything (`all`), the Archived view and any search.
 * Membership and rank are not frame data, so the ids live here. The
 * current project's frames enter the frames store as summaries; other
 * projects' frames (Everything) stay in the scope's own map
 * (`stores/wall-scope-store.ts`). The project feed without a search is
 * store-driven (`stores/wall-feed-store.ts`), so it returns `ids: null` here.
 *
 * - A search waits 200 ms after the last keystroke, aborts the request before
 *   it, and is one ranked page.
 * - A search of the project or Archived refetches 1.5 s after the last
 *   frame event, so new and edited frames find their place.
 * - Everything and Archived page with a cursor.
 * - Everything refetches its first page when the wall shows again, merged
 *   by id: the pages loaded below stay. Entered again, it starts at the top,
 *   since leaving it dropped other projects' cards. Its current-project
 *   cards stay live through the frames store; other projects' cards are not
 *   live (their events go to their own project).
 * - An id whose frame is gone or archived (unarchived, in the Archived view)
 *   leaves at once; a frames-store generation bump starts the scope over.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  listFrameSummaries,
  listWallSummariesAcrossProjects,
  type FrameSummary,
  type FrameSummaryPage,
} from "@/api/frame-listing";
import { useEqualSelector } from "@/hooks/use-equal-selector";
import {
  REGISTERED_FRAME_TYPES,
  frameDataFromSummary,
  type FrameData,
} from "@/lib/frame-data";
import { ingestSummaries } from "@/lib/frame-ingest";
import { sameIds } from "@/lib/same-ids";
import { WALL_PAGE_SIZE } from "@/lib/wall-feed-loader";
import { wallPlacement } from "@/lib/wall-membership";
import { useFramesStore, type FramesState } from "@/stores/frames-store";
import { selectWallContext } from "@/stores/wall-feed-store";
import { useWallScopeStore, type WallScope } from "@/stores/wall-scope-store";

export const WALL_SEARCH_DEBOUNCE_MS = 200;
export const WALL_SCOPE_REFETCH_MS = 1_500;
/** A search is one ranked page: the server's largest. */
const SEARCH_LIMIT = 100;

export interface WallScopeFeed {
  /** Null while the store-driven project feed applies. */
  ids: readonly string[] | null;
  /** Also true while a search waits for its debounce. */
  loading: boolean;
  /** The last request failed; `retry` sends it again. */
  failed: boolean;
  hasMore: boolean;
  loadMore: () => void;
  retry: () => void;
}

interface ScopeState {
  /** The scope and query these ids answer. */
  key: string;
  ids: readonly string[];
  cursor: string | null;
  hasMore: boolean;
  /** Pages loaded: a refreshed first page keeps the ones below it. */
  pages: number;
  loading: boolean;
  failed: boolean;
}

const NO_IDS: readonly string[] = [];

function requestPage(
  projectId: string,
  kind: WallScope["kind"],
  query: string,
  cursor: string | null,
  signal: AbortSignal,
): Promise<FrameSummaryPage> {
  const q = query || undefined;
  const limit = q ? SEARCH_LIMIT : WALL_PAGE_SIZE;
  const after = cursor ?? undefined;
  if (kind === "all") {
    return listWallSummariesAcrossProjects({
      currentProjectId: projectId,
      cursor: after,
      limit,
      q,
      signal,
    });
  }
  const view = kind === "archived" ? "archived" : "wall";
  return listFrameSummaries(projectId, {
    view,
    q,
    limit,
    cursor: after,
    signal,
  });
}

/** Other projects' summaries of a page, as frames for the scope's map. */
function foreignFrames(
  items: readonly FrameSummary[],
  projectId: string,
): FrameData[] {
  return items.flatMap((item) =>
    item.project_id &&
    item.project_id !== projectId &&
    REGISTERED_FRAME_TYPES.has(item.frame_type)
      ? [frameDataFromSummary(item)]
      : [],
  );
}

/** Whether a loaded id shows in `kind`. */
function showsCard(
  kind: WallScope["kind"],
  frames: FramesState,
  foreign: Readonly<Record<string, FrameData>>,
  id: string,
): boolean {
  const own = frames.framesById[id];
  if (kind !== "all")
    return !!own && !!own.archivedAt === (kind === "archived");
  // A current-project card pinned since, or archived, leaves the feed.
  if (own) return wallPlacement(own, selectWallContext(frames)) === "wall";
  const other = foreign[id];
  return !!other && !other.archivedAt;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function useWallScopeFeed(
  projectId: string | null,
  scope: WallScope,
  rawQuery: string,
  /** The wall shows in this window: Everything refreshes as it shows again. */
  visible: boolean,
): WallScopeFeed {
  const query = rawQuery.trim();
  const { kind } = scope;
  const active = !!projectId && (kind !== "project" || query !== "");
  // A replaced frames store drops the frames these ids name: start over.
  const generation = useFramesStore((frames) => frames.generation);
  const key = `${projectId}|${generation}|${kind}|${query}`;
  const [state, setState] = useState<ScopeState | null>(null);
  /** The latest state, which an answer builds on; written with it. */
  const stateRef = useRef(state);
  const commit = useCallback((next: ScopeState) => {
    stateRef.current = next;
    setState(next);
  }, []);
  const controllerRef = useRef<AbortController | null>(null);
  /** The cursor of the latest request: what `retry` sends again. */
  const lastCursorRef = useRef<string | null>(null);
  /** Frames-store writes this hook makes are not frame events. */
  const ingestingRef = useRef(false);

  /**
   * One page: the first (`cursor` null), the next, or (`refresh`) the
   * first again, merged over the pages loaded. A refresh is silent.
   */
  const fetchPage = useCallback(
    async (cursor: string | null, refresh = false) => {
      if (!projectId) return;
      if (!refresh) lastCursorRef.current = cursor;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const before = stateRef.current?.key === key ? stateRef.current : null;
      if (!refresh) {
        commit({
          key,
          ids: before?.ids ?? NO_IDS,
          cursor: before?.cursor ?? null,
          hasMore: before?.hasMore ?? false,
          pages: before?.pages ?? 0,
          loading: true,
          failed: false,
        });
      }
      try {
        const page = await requestPage(
          projectId,
          kind,
          query,
          cursor,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        const loaded = stateRef.current?.key === key ? stateRef.current : null;
        const pageIds = page.items.map((item) => item.id);
        // A search is one ranked page; one loaded page is simply replaced.
        const merge = refresh && !!loaded && loaded.pages > 1 && !query;
        let next: ScopeState;
        if (merge) {
          const fresh = new Set(pageIds);
          next = {
            ...loaded,
            ids: [...pageIds, ...loaded.ids.filter((id) => !fresh.has(id))],
            loading: false,
            failed: false,
          };
        } else if (cursor && loaded) {
          const shown = new Set(loaded.ids);
          next = {
            key,
            ids: [...loaded.ids, ...pageIds.filter((id) => !shown.has(id))],
            cursor: page.nextCursor,
            hasMore: page.hasMore,
            pages: loaded.pages + 1,
            loading: false,
            failed: false,
          };
        } else {
          next = {
            key,
            ids: pageIds,
            cursor: page.nextCursor,
            hasMore: page.hasMore,
            pages: 1,
            loading: false,
            failed: false,
          };
        }
        ingestingRef.current = true;
        try {
          ingestSummaries(
            page.items.filter(
              (item) => (item.project_id ?? projectId) === projectId,
            ),
            projectId,
          );
        } finally {
          ingestingRef.current = false;
        }
        if (kind === "all") {
          useWallScopeStore
            .getState()
            .putForeignFrames(
              foreignFrames(page.items, projectId),
              next.pages === 1,
            );
        }
        commit(next);
      } catch (error) {
        if (controller.signal.aborted || isAbort(error) || refresh) return;
        const failed = stateRef.current;
        if (failed) commit({ ...failed, loading: false, failed: true });
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    [projectId, key, kind, query, commit],
  );

  useEffect(() => {
    if (!active) return;
    // Entered again with its pages loaded: refresh over them. Everything
    // starts again at the top: leaving it dropped other projects' cards.
    const loaded = stateRef.current;
    const refresh =
      kind !== "all" &&
      loaded?.key === key &&
      loaded.pages > 0 &&
      !loaded.loading;
    const timer = window.setTimeout(
      () => void fetchPage(null, refresh),
      query ? WALL_SEARCH_DEBOUNCE_MS : 0,
    );
    return () => {
      window.clearTimeout(timer);
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [active, key, kind, query, fetchPage]);

  // Everything is not live for other projects: refresh as the wall shows.
  const refreshes = active && kind === "all";
  const wasVisibleRef = useRef(visible);
  useEffect(() => {
    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = visible;
    if (!refreshes || !visible || wasVisible) return;
    const loaded = stateRef.current;
    if (loaded?.key === key && loaded.pages > 0 && !loaded.loading) {
      void fetchPage(null, true);
    }
  }, [visible, refreshes, key, fetchPage]);

  const refetches = active && query !== "" && kind !== "all";
  useEffect(() => {
    if (!refetches) return;
    let timer: number | null = null;
    const unsubscribe = useFramesStore.subscribe((next, previous) => {
      if (ingestingRef.current || next.framesById === previous.framesById) {
        return;
      }
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        void fetchPage(null);
      }, WALL_SCOPE_REFETCH_MS);
    });
    return () => {
      unsubscribe();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [refetches, fetchPage]);

  const current = state?.key === key ? state : null;
  const loadMore = useCallback(() => {
    if (current?.cursor && current.hasMore && !current.loading) {
      void fetchPage(current.cursor);
    }
  }, [current, fetchPage]);
  const retry = useCallback(() => {
    if (active) void fetchPage(lastCursorRef.current);
  }, [active, fetchPage]);

  const loadedIds = current?.ids ?? NO_IDS;
  const foreign = useWallScopeStore((scoped) => scoped.foreignFramesById);
  const ids = useFramesStore(
    useEqualSelector(
      (frames: FramesState) =>
        loadedIds.filter((id) => showsCard(kind, frames, foreign, id)),
      sameIds,
    ),
  );

  return {
    ids: active ? ids : null,
    loading: active && (!current || current.loading),
    failed: active && (current?.failed ?? false),
    hasMore: current?.hasMore ?? false,
    loadMore,
    retry,
  };
}
